import { IHttpResponse, IHttpService } from 'angular';
import * as _ from 'underscore';

import { ListResult } from '../../../ts/fetchers/ListResult';
import { CollectionMapper } from '../../../ts/mappers/CollectionMapper';
import { BaseParamsBuilderResourceQuery } from '../../../ts/resources/ResourceQuery';
import { Search, SearchResult } from '../../../ts/resources/Search';
import { FilterOps, ILeafFilterClause, IStringValuedFieldFilterClause, IStringValuedFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import { IGetItemsResult } from '../../../ts/ServerApi/types/IGetItemsResult';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { ServiceLocator } from '../../ServiceLocator';
import { Collection, CollectionItem, AddRemoveCollectionItem, CollectionErroredItem, AddRemoveCollectionItemsData, DeleteCollectionData, CollectionError, CollectionVisibility } from './CollectionModels';
import { ICollectionInfo } from './ICollectionInfo';
import { IContentInfo } from '../../../ts/models/IContentInfo';

export const COLLECTIONS_URI: string = 'collections/collections';

export interface GetCollectionsResponse extends IGetItemsResult {
  readonly items?: Collection[];
}

export interface GetCollectionItemsResponse extends IGetItemsResult {
  readonly items?: CollectionItem[];
}

export interface AddRemoveItemsResponse {
  readonly response: number;
  readonly errors: CollectionErroredItem[];
}

export interface UpdateCollectionData {
  readonly name?: string;
  readonly description?: string;
  readonly visibility?: CollectionVisibility;
  readonly ownerLuid?: string;
}

export interface DeleteResponse {
  readonly response: string[];
  readonly errors: CollectionError[];
}

const FilterOpsToString = (op: string): string => {
  if (op === FilterOps.eq) {
    return ':';
  } else if (op === FilterOps.gte) {
    return '>';
  } else if (op === FilterOps.lte) {
    return '<';
  }
};

export class CollectionsResourceQuery extends BaseParamsBuilderResourceQuery<ICollectionInfo> {
  public getSlice(start: number, size: number): ng.IPromise<ListResult<ICollectionInfo>> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const filters: ILeafFilterClause[] = this.getFilters();
    const matchesFilter = filters.find((clause) => clause.operator === FilterOps.matches);
    let filterClause = '';
    if (filters.length > 0) {
      filterClause =
        _.without(filters, matchesFilter)
          .map((clause) => {
            const valueClause = clause as IStringValuedFieldFilterClause;
            return valueClause.field + FilterOpsToString(valueClause.operator) + '"' + valueClause.value + '"';
          })
          .join(' AND ');
    }

    if (matchesFilter) {
      const contentTypeClause = 'type:collection';
      if (filterClause.length > 0) {
        filterClause = ' AND ' + filterClause;
      }
      let search = new Search()
        .withOrder(this.getOrder())
        .page(start)
        .limit(size)
        .search((matchesFilter as IStringValuedFilterClause).value)
        .filter(contentTypeClause + filterClause);
      return search.getResults().then((searchResult: SearchResult) => {
        return {
          items: searchResult.items as any[],
          total: searchResult.total,
          index: searchResult.pageIndex
        };
      });
    }

    let orderClause = '';
    const order: IOrderClause[] = this.getOrder();
    if (order.length > 0) {
      orderClause = 'order=' +
        order
          .map((clause) => clause.field + ' ' + (clause.ascending ? 'asc' : 'desc'))
          .join(',');
    }

    let pagingClause = '';
    if (start != null) {
      pagingClause += '&startIndex=' + start;
    }
    if (size != null) {
      pagingClause += '&maxItems=' + size;
    }

    if (filterClause.length > 0) {
      filterClause = '&filter=' + filterClause;
    }

    let spacer: string = '';
    let userAndSiteId: string = '';

    let query = '';
    if (orderClause || filterClause || pagingClause || userAndSiteId) {
      query = (userAndSiteId || '?') + spacer + orderClause + filterClause + pagingClause;
    }

    const url = COLLECTIONS_URI + query;

    const requestConfig = collectionsRequestConfig('GET', url);

    return http(requestConfig)
      .then((response: IHttpResponse<GetCollectionsResponse>) => {
        return CollectionMapper.mapList(response, start);
      })
      .catch((reason) => {
        return ListResult.empty<ICollectionInfo>();
      });
  }

  public create(name?: string): ng.IPromise<ICollectionInfo> {
    const http = ServiceLocator.get<IHttpService>('$http');
    const requestConfig = collectionsRequestConfig('POST', COLLECTIONS_URI, { name, desc: '' });
    return http(requestConfig).then(CollectionMapper.mapsSingle);
  }

  public delete(luids: string[]): ng.IPromise<DeleteCollectionData> {
    const http = ServiceLocator.get<IHttpService>('$http');
    const luidsParam = luids.join(',');

    const url = COLLECTIONS_URI + '?luids=' + luidsParam;
    const requestConfig = collectionsRequestConfig('DELETE', url);

    return http(requestConfig).then((response: IHttpResponse<DeleteResponse>) => {
      return CollectionMapper.mapDeleteCollectionsResponse(response);
    });
  }

  public update(collectionLuid: string, updateData: UpdateCollectionData): ng.IPromise<{}> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const url = COLLECTIONS_URI + '/' + collectionLuid;
    const requestConfig = collectionsRequestConfig('PATCH', url, updateData);

    return http(requestConfig).then(() => { return {}; });
  }

  public getByLuid(luid: string): ng.IPromise<ICollectionInfo> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const url = COLLECTIONS_URI + '/' + luid;
    const requestConfig = collectionsRequestConfig('GET', url);

    return http(requestConfig).then(CollectionMapper.mapsSingle);
  }

  public getCount(): ng.IPromise<number> {
    return this.getSlice(0, 0).then((slice: ListResult<ICollectionInfo>) => slice.total);
  }

  public getFirst(): ng.IPromise<ICollectionInfo> {
    throw new Error('CollectionsResourceQuery getFirst not implemented');
  }
}

export class CollectionItemsResourceQuery extends BaseParamsBuilderResourceQuery<any> {

  public constructor(private collectionLuid: string) {
    super();
  }

  public getSlice(start: number, size: number): ng.IPromise<ListResult<any>> {
    const http = ServiceLocator.get<IHttpService>('$http');

    // TODO: As filters and sorts are added, the url will need a ? if there is not siteAndUser. See GetCollections.
    const url = COLLECTIONS_URI + '/' + this.collectionLuid + '/items';
    const requestConfig = collectionsRequestConfig('GET', url);

    return http(requestConfig)
      .then((response: IHttpResponse<GetCollectionItemsResponse>) => {
        return CollectionMapper.mapItemsList(response, start);
      })
      .catch((reason) => {
        return ListResult.empty<any>();
      });
    }

  public getCount(): ng.IPromise<number> {
    throw new Error('CollectionsResourceQuery getCount not implemented');
  }

  public getFirst(): ng.IPromise<ICollectionInfo> {
    throw new Error('CollectionsResourceQuery getFirst not implemented');
  }

  public addItemsToCollection(items: IContentInfo[]): ng.IPromise<AddRemoveCollectionItemsData> {
    const http = ServiceLocator.get<IHttpService>('$http');

    let collectionItems: AddRemoveCollectionItem[] = [];
    for (let item of items) {
      collectionItems.push({
        contentLuid: item.luid,
        contentType: item.contentType,
        contentName: item.name,
        position: '0' // TFS 1090593
      });
    }

    const url = COLLECTIONS_URI + '/' + this.collectionLuid + '/items';
    const requestConfig = collectionsRequestConfig('PUT', url, { items: collectionItems });

    return http(requestConfig).then((response: IHttpResponse<AddRemoveItemsResponse>) => {
      return CollectionMapper.mapAddRemoveItemsResponse(response);
    });
  }

  public removeItemsFromCollection(items: IContentInfo[]): ng.IPromise<AddRemoveCollectionItemsData> {
    const http = ServiceLocator.get<IHttpService>('$http');
    let url = COLLECTIONS_URI + '/' + this.collectionLuid + '/items';

    let itemsToRemove: String[] = [];

    for(let item of items) {
      itemsToRemove.push(item.luid + ' ' + item.contentType);
    }

    url += '?luidsAndContentTypes=' + itemsToRemove.join(',');

    const requestConfig = collectionsRequestConfig('DELETE', url);

    return http(requestConfig).then((response: IHttpResponse<AddRemoveItemsResponse>) => {
      return CollectionMapper.mapAddRemoveItemsResponse(response);
    });
  }
}

function collectionsRequestConfig(method: string, url: string, data?: any): ng.IRequestConfig {
  return {
    method: method,
    url: url,
    data: data,
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'}
  };
}